import { useState } from 'react';
import axios from 'axios';
import './SpotQuotesForm.css';
import LoadingDialog from './LoadingDialog';
import CustomAlert from './CustomAlert';
import { useNavigate } from 'react-router-dom';
import BACKEND_URL from '../config'; // Import the BACKEND_URL from the config file

const SpotQuotesForm = () => {
    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
    const initialFormState = {
        referenceNumber: '',
        shipper: '',
        shipFromAddress: '',
        shipFromCity: '',
        shipFromState: '',
        shipFromZip: '',
        consignee: '',
        consigneeAddress: '',
        consigneeCity: '',
        consigneeState: '',
        consigneeZip: '',
        pickupDate: today, // Set default pickup date to today
        weight: '',
        pallets: '',
        modeType: 'FTL', // Default mode type
        freightClass: '' // Add class field
    };

    const [formData, setFormData] = useState(initialFormState);
    const [quoteResponse, setQuoteResponse] = useState(null); // State for quote response
    const [isLoading, setIsLoading] = useState(false); // State for loading dialog during form submission
    const [isBooking, setIsBooking] = useState(false); // State for loading dialog during booking
    const [alert, setAlert] = useState({ show: false, title: '', message: '' }); // State for custom alert
    const navigate = useNavigate();
    const usStates = [
        "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
    ];

    const classOptions = [
        "", "50", "55", "60", "65", "70", "77.5", "85", "92.5", "100", "110", "125", "150", "175", "200", "250", "300", "400", "500"
    ];

    // Date validation function
    const isValidDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = () => {
        if (!formData.shipFromAddress) {
            setAlert({ show: true, title: 'Error', message: 'Ship From Address cannot be blank' });
            return false;
        }
        if (!formData.consigneeAddress) {
            setAlert({ show: true, title: 'Error', message: 'Consignee Address cannot be blank' });
            return false;
        }
        if (!/^\d{5}$/.test(formData.shipFromZip)) {
            setAlert({ show: true, title: 'Error', message: 'Ship From Zip must be at least 5 numerical digits' });
            return false;
        }
        if (!/^\d{5}$/.test(formData.consigneeZip)) {
            setAlert({ show: true, title: 'Error', message: 'Consignee Zip must be at least 5 numerical digits' });
            return false;
        }
        if (!isValidDate(formData.pickupDate)) {
            setAlert({ show: true, title: 'Error', message: 'Invalid Pickup Date.' });
            return false;
        }
        if (new Date(formData.pickupDate) <= new Date(today)) {
            setAlert({ show: true, title: 'Error', message: 'Pickup Date needs to be later than today' });
            return false;
        }
        if (formData.weight <= 0) {
            setAlert({ show: true, title: 'Error', message: 'Weight must be greater than 0' });
            return false;
        }
        if (formData.pallets <= 0) {
            setAlert({ show: true, title: 'Error', message: 'Pallets must be greater than 0' });
            return false;
        }

        if (formData.modeType === 'LTL' && !formData.freightClass) {
            setAlert({ show: true, title: 'Error', message: 'Freight Class is required when Mode Type is LTL' });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true); // Show loading dialog
        try {
            const response = await axios.post(`${BACKEND_URL}/api/spot-quotes`, formData, { withCredentials: true });
            setQuoteResponse(response.data); // Set quote response data
            setAlert({ show: true, title: 'Success', message: 'Form submitted successfully!' });
        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Redirect to login page if 401 error is encountered
                navigate('/tms');
            } else {
                console.error('Error submitting form:', error);
                setAlert({ show: true, title: 'Error', message: 'There was an error submitting the form.' });
            }
        } finally {
            setIsLoading(false); // Hide loading dialog
        }
    };

    const handleBookShipment = async (quote, event) => {
        event.stopPropagation();
        event.preventDefault();
        setIsBooking(true); // Show loading dialog for booking
        try {
            // Combine formData and quote into a single object
            const combinedData = {
                formData, // Includes form fields such as shipper, referenceNumber, etc.
                quote    // Includes the quote data received from the response
            };

            // Send the combined data to the backend
            await axios.post(`${BACKEND_URL}/api/book-shipment-spot-quotes`, combinedData, { withCredentials: true });
            
            setAlert({ show: true, title: 'Success', message: 'Shipment booked successfully!' });
            //setFormData(initialFormState); // Reset form fields
            setQuoteResponse(null); // Clear quote summaries table
        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Redirect to login page if 401 error is encountered
                navigate('/tms');
            } else {
                console.error('Error booking shipment:', error);
                setAlert({ show: true, title: 'Error', message: 'There was an error booking the shipment.' });
            }
        } finally {
            setIsBooking(false); // Hide loading dialog for booking
        }
    };

    const handleClear = () => {
        setFormData(initialFormState); // Reset form fields
        setQuoteResponse(null); // Clear quote summaries table
    };

    const handleCloseAlert = () => {
        setAlert({ show: false, title: '', message: '' });
    };

    return (
        <>
            {(isLoading || isBooking) && <LoadingDialog />} {/* Show loading dialog */}

            <CustomAlert
                show={alert.show}
                handleClose={handleCloseAlert}
                title={alert.title}
                message={alert.message}
            />

            <h1>Spot Quotes</h1>

            {quoteResponse 
                ?
                    <div className="table-container">
                        <div className="table-content-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Carrier</th>
                                        <th>Quote ID</th>
                                        <th>Total Cost</th>
                                        <th className="book-shipment-th">Book Shipment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(quoteResponse).map((key) => (
                                        <tr key={key}>
                                            <td>{quoteResponse[key].carrier}</td>
                                            <td>{quoteResponse[key].quote_id}</td>
                                            <td>{quoteResponse[key].total_cost}</td>
                                            <td>
                                                <button onClick={(e) => handleBookShipment(quoteResponse[key], e)} disabled={isLoading || isBooking}>Book Shipment</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                : 
                    <form onSubmit={handleSubmit} className="spot-quotes-form">
                        <div className="input-label-ctnr">
                            <label htmlFor="modeType">Mode Type</label>
                            <div className="radio-group-horizontal">
                                <div>
                                    <input
                                        type="radio"
                                        id="ftl"
                                        name="modeType"
                                        value="FTL"
                                        checked={formData.modeType === 'FTL'}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="ftl">FTL</label>
                                </div>
                                <div>
                                    <input
                                        type="radio"
                                        id="ltl"
                                        name="modeType"
                                        value="LTL"
                                        checked={formData.modeType === 'LTL'}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="ltl">LTL</label>
                                </div>
                                <div>
                                    <input
                                        type="radio"
                                        id="interModal"
                                        name="modeType"
                                        value="InterModal"
                                        checked={formData.modeType === 'InterModal'}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="interModal">InterModal</label>
                                </div>
                            </div>
                        </div>

                        <div className="input-label-ctnr">
                            <label htmlFor="referenceNumber">Reference Number</label>
                            <input type="text" name="referenceNumber" value={formData.referenceNumber} onChange={handleChange} />
                        </div>

                        <fieldset>
                            <legend>Shipper Details</legend>
                            <div className="input-label-ctnr">
                                <label htmlFor="shipper">Shipper</label>
                                <input type="text" name="shipper" value={formData.shipper} onChange={handleChange} />
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="shipFromAddress">Ship From Address</label>
                                <input type="text" name="shipFromAddress" value={formData.shipFromAddress} onChange={handleChange} required />
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="shipFromCity">Ship From City</label>
                                <input type="text" name="shipFromCity" value={formData.shipFromCity} onChange={handleChange} required />
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="shipFromState">Ship From State</label>
                                <select className="select-state" name="shipFromState" value={formData.shipFromState} onChange={handleChange} required>
                                    <option value="">Select a state</option>
                                    {usStates.map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="shipFromZip">Ship From Zip</label>
                                <input className="input-zipcode" type="text" name="shipFromZip" value={formData.shipFromZip} onChange={handleChange} required />
                            </div>
                        </fieldset>

                        <fieldset>
                            <legend>Consignee Details</legend>
                            <div className="input-label-ctnr">
                                <label htmlFor="consignee">Consignee</label>
                                <input type="text" name="consignee" value={formData.consignee} onChange={handleChange} />
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="consigneeAddress">Consignee Address</label>
                                <input type="text" name="consigneeAddress" value={formData.consigneeAddress} onChange={handleChange} required />
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="consigneeCity">Consignee City</label>
                                <input type="text" name="consigneeCity" value={formData.consigneeCity} onChange={handleChange} required />
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="consigneeState">Consignee State</label>
                                <select className="select-state" name="consigneeState" value={formData.consigneeState} onChange={handleChange} required>
                                    <option value="">Select a state</option>
                                    {usStates.map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="input-label-ctnr">
                                <label htmlFor="consigneeZip">Consignee Zip</label>
                                <input className="input-zipcode" type="text" name="consigneeZip" value={formData.consigneeZip} onChange={handleChange} required />
                            </div>
                        </fieldset>

                        <div className="input-label-ctnr">
                            <label htmlFor="pickupDate">Pickup Date</label>
                            <input className="input-date" type="date" name="pickupDate" value={formData.pickupDate} onChange={handleChange} required />
                        </div>

                        <fieldset>
                            <legend>Freight Details</legend>
                            <div className="input-label-ctnr">
                                <label htmlFor="freightClass">Freight Class</label>
                                <select className="freight-class" name="freightClass" value={formData.freightClass} onChange={handleChange}>
                                    {classOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-label-ctnr">
                                <label htmlFor="weight">Weight</label>
                                <input className="input-weight-pallets" type="number" name="weight" min="0" value={formData.weight} onChange={handleChange} />
                            </div>

                            <div className="input-label-ctnr">
                                <label htmlFor="pallets">Pallets</label>
                                <input className="input-weight-pallets" type="number" name="pallets" min="0" value={formData.pallets} onChange={handleChange} />
                            </div>
                        </fieldset>

                        <div className="form-controls">
                            <button className="submit-btn" type="submit" disabled={isLoading || isBooking}>Submit</button>
                            <button className="cancel-btn" type="button" onClick={handleClear}>Clear</button>
                        </div>
                    </form>
            }
        </>
    );
};

export default SpotQuotesForm;
